import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslationService } from 'src/app/core/services/translation/translation.service';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * File Upload Molecule
 */
export class FileUploadComponent implements OnInit, OnDestroy {
  labels = {
    chooseFile: 'choose-file',
    noFileChosen: 'no-file-chosen',
  };
  @Input() abstractControl?: FormControl<File | null>;
  _accept?: string;
  /**
   * accepted file types
   */
  get accept() {
    return this._accept;
  }

  /**
   * Chooses accepted mime type for file input
   * @param {string} value
   */
  @Input() set accept(value: string | undefined) {
    this._accept = value;
    this.checkFileType(this.abstractControl?.value || undefined);
  }

  @ViewChild('fileUpload') upload!: ElementRef<HTMLInputElement>;
  selectedFile?: File | null;
  fileError: string = '';
  loading: boolean = false;
  fileChangedEvent: null | Event = null;
  fileNameControl = new FormControl<string>('');
  private subsink = new Subscription();

  /**
   * Set up file url if avaiable from form control
   */
  async ngOnInit() {
    await this.translationService.translateObject(this.labels);
    this.fileNameControl.setValue(this.labels.noFileChosen);
    if (this.abstractControl instanceof AbstractControl) {
      this.selectedFile = this.abstractControl.value;
      if (this.selectedFile != null) {
        this.fileNameControl.setValue(
          (this.abstractControl.value as File)?.name
        );
      } else {
        this.fileNameControl.setValue(this.labels.noFileChosen);
      }
      this.subsink.add(
        this.abstractControl?.valueChanges.subscribe((value) => {
          if (value instanceof File) {
            this.fileNameControl.setValue(value.name);
            this.checkFileType(value);
          } else {
            this.fileNameControl.setValue(this.labels.noFileChosen);
            this.upload.nativeElement.value = '';
          }
        })
      );
    }
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Checks if a file has the accepted file type
   * @param {File} file file to check
   */
  checkFileType(file?: File) {
    if (!file || !this.accept) {
      return;
    }
    if (file.type !== this.accept) {
      const error = { 'invalid-file-format': 'invalid-file-format' };
      this.abstractControl?.setErrors(error);
      this.fileNameControl.setErrors(error);
      this.fileNameControl.markAsTouched();
    } else {
      this.abstractControl?.setErrors(null);
      this.fileNameControl.setErrors(null);
    }
  }

  /**
   * @constructor
   */
  constructor(private readonly translationService: TranslationService) {}

  /**
   * Called when a file is selected
   * @param {Event} event object used on cropper
   */
  onFileSelected(event: Event) {
    this.fileError = '';
    const target = event.target as HTMLInputElement;
    let file: File;
    if (target.files) {
      file = target.files[0];
    } else {
      // TODO: improve errors
      throw new Error('error reading file');
    }
    this.setFileUrl(file, event);
  }

  /**
   * Retrieves base64 encoded file url from a file object
   * @param {File} file get url from file
   * @param {Event} _event event for fileChanged property
   */
  setFileUrl(file: File, _event: Event) {
    if (this.isFileHuge(file)) {
      this.fileError = 'file cannot exceed 2 MB';
      return;
    }
    this.abstractControl?.setValue(file);
  }

  /**
   * @param {File} file
   * @return {boolean}
   */
  isFileHuge(file: File): boolean {
    const max = 5000000000; // 5GB;
    return file?.size > max;
  }
}
